// Agent Chat Types - Shared between frontend and backend
// Used for WebSocket events, message types, and status tracking

// ============================================================
// Message Types
// ============================================================

export const MessageType = {
  USER: 'user',
  ASSISTANT: 'assistant',
  TOOL_USE: 'tool_use',
  TOOL_RESULT: 'tool_result',
  TEXT: 'text',
  ERROR: 'error',
  SYSTEM: 'system',
} as const;

export type MessageType = (typeof MessageType)[keyof typeof MessageType];

// ============================================================
// Stream Message Types (Claude SDK protocol)
// ============================================================

export const StreamType = {
  STREAM: 'stream',
} as const;

export type StreamType = (typeof StreamType)[keyof typeof StreamType];

// ============================================================
// Task Status (for streaming/UI state)
// ============================================================

export const TaskStatus = {
  QUEUED: 'queued',
  RUNNING: 'running',
  COMPLETED: 'completed',
  PAUSED: 'paused',
  ERROR: 'error',
} as const;

export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

// ============================================================
// Tool Status
// ============================================================

export const ToolStatus = {
  RUNNING: 'running',
  COMPLETED: 'completed',
  ERROR: 'error',
} as const;

export type ToolStatus = (typeof ToolStatus)[keyof typeof ToolStatus];

// ============================================================
// Connection State (for WebSocket connection tracking)
// ============================================================

export const ConnectionState = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
} as const;

export type ConnectionState = (typeof ConnectionState)[keyof typeof ConnectionState];

// ============================================================
// Socket.io Built-in Events (client-side)
// ============================================================

export const SocketEvent = {
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  CONNECT_ERROR: 'connect_error',
  RECONNECT: 'reconnect',
  RECONNECT_ATTEMPT: 'reconnect_attempt',
  RECONNECT_ERROR: 'reconnect_error',
  RECONNECT_FAILED: 'reconnect_failed',
} as const;

export type SocketEvent = (typeof SocketEvent)[keyof typeof SocketEvent];

// ============================================================
// Socket Event Names (Custom Application Events)
// ============================================================

export const AgentSocketEvent = {
  // Client → Server
  START_CLAUDE_SESSION: 'start-claude-session',
  JOIN_SESSION: 'join-session',
  LEAVE_SESSION: 'leave-session',
  STOP_CHAT: 'stop-chat',
  CHECK_HEALTH: 'check-health',
  IMAGE_GEN_REQUEST: 'image-gen-request',
  MEDIA_LIBRARY_SEARCH_REQUEST: 'media-library-search-request',
  MEDIA_LIBRARY_DOWNLOAD_REQUEST: 'media-library-download-request',
  LIST_PROJECT_ASSETS_REQUEST: 'list-project-assets-request',

  // Server → Client
  CONNECTED: 'connected',
  CHAT_RESPONSE: 'chat-response',
  CHAT_ERROR: 'chat-error',
  CHAT_STARTED: 'chat-started',
  CHAT_STOPPED: 'chat-stopped',
  CHAT_COMPLETE: 'chat-complete',
  USER_MESSAGE: 'user-message',
  TASK_CREATED: 'task-created',
  TASK_QUEUED: 'task-queued', // Task queued waiting for container to resume
  TASK_CANCELLED: 'task-cancelled',
  TASK_PAUSED: 'task-paused',
  STREAM_COMPLETE: 'stream-complete',
  TOOL_STARTED: 'tool-started',
  TOOL_COMPLETED: 'tool-completed',
  ASSISTANT_ENTRY_SAVED: 'assistant-entry-saved', // First assistant entry saved to database
  HITL_PERSISTED: 'hitl-persisted',
  HITL_ACTION_CLICKED: 'hitl-action-clicked',
  HITL_REALTIME: 'hitl-realtime',
  AUTO_COMMIT_CREATED: 'auto-commit-created',
  AUTO_COMMIT_FAILED: 'auto-commit-failed',
  SESSION_TITLE_UPDATED: 'session-title-updated',
  HEALTH_STATUS: 'health-status',
  IMAGE_GEN_RESPONSE: 'image-gen-response',
  MEDIA_LIBRARY_SEARCH_RESPONSE: 'media-library-search-response',
  MEDIA_LIBRARY_DOWNLOAD_RESPONSE: 'media-library-download-response',
  LIST_PROJECT_ASSETS_RESPONSE: 'list-project-assets-response',
} as const;

export type AgentSocketEvent = (typeof AgentSocketEvent)[keyof typeof AgentSocketEvent];

// ============================================================
// Socket Event Payloads
// ============================================================

export interface UserMessagePayload {
  id: string;
  entryId?: string;
  userMessageTempId?: string;
  content: string;
  timestamp: string;
  sessionId: string;
  role: 'user';
}

export interface ChatResponsePayload {
  type: 'stream' | 'error';
  data?: {
    type: 'text' | 'tool_use';
    content?: string;
  };
  sessionId: string;
  taskId: string;
  userMessageId?: string;
  parentEntryId?: string;
}

export interface TaskCreatedPayload {
  taskId: string;
  sessionId: string;
  clientMessageId?: string;
  parentEntryId?: string;
  userMessageId?: string;
}

export interface TaskQueuedPayload {
  taskId: string;
  sessionId: string;
  clientMessageId?: string;
  parentEntryId?: string;
  userMessageId?: string;
  reason: 'container_paused'; // Why the task is queued
}

export interface ChatStartedPayload {
  id: string; // sessionId
  projectId?: string;
}

export interface StreamCompletePayload {
  sessionId: string;
  taskId: string;
  assistantMessageId: string;
  toolUsage?: Record<string, string[]>;
  userMessageId?: string;
  parentEntryId?: string;
  completedAt?: string;
}

export interface ToolStartedPayload {
  sessionId: string;
  taskId: string;
  entryId: string;
  toolUseId: string;
  toolName: string;
  toolInput?: Record<string, unknown>;
  textPosition?: number;
  userMessageId?: string;
  assistantMessageId?: string; // Text entry ID saved before tool (for early ID update)
}

export interface ToolCompletedPayload {
  sessionId: string;
  taskId: string;
  toolUseId: string;
  isError?: boolean;
  resultSummary?: string;
  errorMessage?: string;
  userMessageId?: string;
  completedAt?: string;
}

export interface AssistantEntrySavedPayload {
  sessionId: string;
  taskId: string;
  entryId: string; // The real database entry ID
  userMessageId?: string;
}

export interface TaskCancelledPayload {
  sessionId: string;
  taskId: string;
  reason?: string;
  timestamp?: string;
}

export interface TaskPausedPayload {
  taskId: string;
  sessionId?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface HitlPersistedPayload {
  sessionId: string;
  taskId: string;
  messageId: string;
  content: string;
  timestamp: string;
}

export interface HitlActionClickedPayload {
  messageId: string;
  actionId: string;
  sessionId?: string;
}

export interface ChatErrorPayload {
  message: string;
  code?: string;
  error?: string;
}

export interface AutoCommitCreatedPayload {
  sessionId: string;
  commitId: string;
  commitSha: string;
  filesCount: number;
  message: string;
  changedFiles?: string[];
}
